'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { insightArticles } from '@/lib/insightsData';

const categories = ['All', 'Corporate & Commercial', 'Arbitration & ADR', 'Fintech & Technology', 'Property & Real Estate', 'Technology & Digital Law'];

export default function InsightsList() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = insightArticles?.filter((a) => {
    const matchCat = activeCategory === 'All' || a?.category === activeCategory;
    const matchSearch = searchQuery === '' ||
      a?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      a?.excerpt?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchCat && matchSearch;
  });

  const featured = filtered?.[0];
  const rest = filtered?.slice(1);

  return (
    <section className="py-16 px-6 md:px-12" aria-label="Insights Articles">
      <div className="max-w-7xl mx-auto">
        {/* Filters + Search */}
        <div className="flex flex-col md:flex-row gap-6 items-start md:items-center justify-between mb-12 pb-8 border-b border-border">
          <div className="flex flex-wrap gap-2">
            {categories?.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-xs uppercase tracking-widest px-4 py-2 font-semibold transition-colors duration-200 ${
                  activeCategory === cat
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border text-muted-foreground hover:text-foreground hover:border-foreground'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <input
              type="search"
              placeholder="Search insights..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="form-input pr-8"
              aria-label="Search insights"
            />
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" className="absolute right-0 top-3 text-muted-foreground pointer-events-none" aria-hidden="true">
              <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
          </div>
        </div>

        {filtered?.length === 0 && (
          <div className="text-center py-24">
            <p className="text-muted-foreground text-sm">No articles found matching your criteria.</p>
          </div>
        )}

        {featured && (
          <div className="mb-12">
            <Link href={`/insights/${featured?.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 border border-border hover:border-primary/50 transition-colors duration-300 overflow-hidden">
              <div className="relative min-h-[300px] overflow-hidden">
                <AppImage
                  src={featured?.image}
                  alt={featured?.imageAlt}
                  fill
                  className="object-cover photo-grayscale"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
              </div>
              <div className="p-10 bg-card flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-xs uppercase tracking-widest text-primary font-semibold">{featured?.category}</span>
                    <span className="text-xs text-muted-foreground">Featured</span>
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl italic text-foreground mb-4 leading-tight group-hover:text-primary transition-colors duration-300">
                    {featured?.title}
                  </h2>
                  <p className="text-sm text-muted-foreground font-light leading-relaxed">
                    {featured?.excerpt}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-muted-foreground">{featured?.date}</span>
                    <span className="text-xs text-muted-foreground">{featured?.readTime}</span>
                  </div>
                  <span className="text-xs uppercase tracking-widest text-primary font-semibold group-hover:gap-2 flex items-center gap-1 transition-all">
                    Read
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" className="group-hover:translate-x-1 transition-transform" aria-hidden="true">
                      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 12h14m-7-7l7 7l-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Article grid */}
        {rest?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-border divide-y md:divide-y-0 divide-border">
            {rest?.map((article, i) => (
              <Link
                key={article?.slug}
                href={`/insights/${article?.slug}`}
                className={`group block p-8 service-card-hover hover:bg-card transition-colors duration-300 ${
                  i % 3 !== 2 ? 'lg:border-r lg:border-border' : ''
                } ${
                  i % 2 !== 1 ? 'md:border-r md:border-border lg:border-r-0' : ''
                } ${
                  i < rest?.length - (rest?.length % 3 || 3) ? 'lg:border-b lg:border-border' : ''
                }`}
              >
                <div className="relative w-full h-48 overflow-hidden mb-6 bg-muted">
                  <AppImage
                    src={article?.image}
                    alt={article?.imageAlt}
                    fill
                    className="object-cover photo-grayscale"
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs uppercase tracking-widest text-primary font-semibold">{article?.category}</span>
                </div>
                <h3 className="font-display text-lg italic text-foreground mb-3 leading-tight group-hover:text-primary transition-colors duration-300">
                  {article?.title}
                </h3>
                <p className="text-xs text-muted-foreground font-light leading-relaxed mb-4 line-clamp-2">
                  {article?.excerpt}
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-border">
                  <span className="text-xs text-muted-foreground">{article?.date}</span>
                  <span className="text-xs text-muted-foreground">{article?.readTime}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}